import { __awaiter, __generator, __rest, __assign } from 'tslib';

// Standard page action attribute names for consistent naming conventions.
var SHOPPER_ID = 'shopperId';
var SITE = 'site';
var TENANT = 'tenant';
var LOCALE = 'locale';
var LOG_PREFIX = '[NewRelicTracker]';

var windowInvalid = function () { return typeof window === "undefined"; };
/**
 * Retrieve an agent from the window stored under the given agentId.
 *
 * @param agentId
 * @param options
 * @param options.enforceAgentExist By default this function will return whatever window[agentId] is regardless of if
 * it is undefined. By setting this property to true, this function will throw an error if window[agentId] is undefined.
 */
var getAgentFromWindow = function (agentId, options) {
    if (options === void 0) { options = { enforceAgentExists: false }; }
    if (windowInvalid()) {
        throw new Error('window is undefined');
    }
    var agent = window[agentId];
    if (options.enforceAgentExists && typeof agent === 'undefined') {
        throw new Error("Agent ".concat(agentId, " does not exist"));
    }
    return agent;
};

/**
 * Retry wrapper around retrieving an agent from the window as it might not have been initialized and saved yet.
 */
var retryWrapper = function (agentId, debug) {
    if (debug === void 0) { debug = false; }
    return __awaiter(void 0, void 0, void 0, function () {
        var agent, i;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    i = 0;
                    _a.label = 1;
                case 1:
                    if (!(i < 3)) return [3 /*break*/, 4];
                    // wait .5 sec before fetching agent from the window
                    return [4 /*yield*/, new Promise(function (resolve) {
                            setTimeout(resolve, 500);
                        })];
                case 2:
                    // wait .5 sec before fetching agent from the window
                    _a.sent();
                    agent = getAgentFromWindow(agentId);
                    if (typeof agent === 'undefined') {
                        if (debug) {
                            console.warn("".concat(LOG_PREFIX, " Agent ").concat(agentId, " does not exist... retrying..."));
                        }
                    }
                    else {
                        return [3 /*break*/, 4];
                    }
                    _a.label = 3;
                case 3:
                    i += 1;
                    return [3 /*break*/, 1];
                case 4:
                    if (typeof agent === 'undefined') {
                        throw new Error("Agent ".concat(agentId, " does not exist"));
                    }
                    return [2 /*return*/, agent];
            }
        });
    });
};
/**
 * An initialization function to set static application data on the new relic agent which will then be included on future
 * PageAction and JavaScriptError events.
 *
 * @param agentId
 * @param staticApplicationData data to included on all PageAction and JavaScriptError events
 * @param staticApplicationData.shopperId
 * @param staticApplicationData.tenant
 * @param staticApplicationData.site
 * @param staticApplicationData.locale
 * @param staticApplicationData.additionalAttributes map of additional attributes to be included on the events where each
 * entry will be a new column
 * @param debug - if set to true, will provide additional console debug information in case of errors.
 */
var initializeNewRelicTrackingContainer = function (_a) {
    var agentId = _a.agentId, staticApplicationData = _a.staticApplicationData, _b = _a.debug, debug = _b === void 0 ? false : _b;
    return __awaiter(void 0, void 0, void 0, function () {
        var agent, _c, shopperId, _d, tenant, _e, site, _f, locale, _g, additionalAttributes, err_1;
        return __generator(this, function (_h) {
            switch (_h.label) {
                case 0:
                    _c = staticApplicationData.shopperId, shopperId = _c === void 0 ? '' : _c, _d = staticApplicationData.tenant, tenant = _d === void 0 ? '' : _d, _e = staticApplicationData.site, site = _e === void 0 ? '' : _e, _f = staticApplicationData.locale, locale = _f === void 0 ? '' : _f, _g = staticApplicationData.additionalAttributes, additionalAttributes = _g === void 0 ? {} : _g;
                    _h.label = 1;
                case 1:
                    _h.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, retryWrapper(agentId, debug)];
                case 2:
                    agent = _h.sent();
                    return [3 /*break*/, 4];
                case 3:
                    err_1 = _h.sent();
                    if (debug) {
                        console.error("".concat(LOG_PREFIX, " Unable to initialize New Relic Tracking Container - ").concat(err_1.message));
                    }
                    return [2 /*return*/];
                case 4:
                    try {
                        agent.setCustomAttribute(SHOPPER_ID, shopperId);
                        agent.setCustomAttribute(TENANT, tenant);
                        agent.setCustomAttribute(SITE, site);
                        agent.setCustomAttribute(LOCALE, locale);
                        Object.entries(additionalAttributes).forEach(function (_a) {
                            var key = _a[0], value = _a[1];
                            agent.setCustomAttribute(key, value);
                        });
                    }
                    catch (err) {
                        if (debug) {
                            console.error("".concat(LOG_PREFIX, " Something went wrong attempting to custom attributes on agent: ").concat(agentId, " - ").concat(err.message));
                        }
                    }
                    return [2 /*return*/];
            }
        });
    });
};
/**
 * A function to add additional attributes to the tracking container after initialization.
 * For example, if we want to track the active experiments for a shopper (and what variation are were in):
 * additionalAttributes = {
 *     activeExperiments: 'om_ab_test:variation_1,om_ab_test_2:variation_2'
 * }
 * activeExperiments will then become a new column in any future PageAction and JavaScriptError events.
 *
 * @param agentId
 * @param additionalAttributes
 * @param debug - if set to true, will provide additional console debug information in case of errors.
 */
var addAdditionalAttributes = function (agentId, additionalAttributes, debug) {
    if (debug === void 0) { debug = false; }
    return __awaiter(void 0, void 0, void 0, function () {
        var agent, err_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, retryWrapper(agentId, debug)];
                case 1:
                    agent = _a.sent();
                    return [3 /*break*/, 3];
                case 2:
                    err_2 = _a.sent();
                    if (debug) {
                        console.error("".concat(LOG_PREFIX, " Unable to add additional attributes - ").concat(err_2.message));
                    }
                    return [2 /*return*/];
                case 3:
                    try {
                        Object.entries(additionalAttributes).forEach(function (_a) {
                            var key = _a[0], value = _a[1];
                            agent.setCustomAttribute(key, value);
                        });
                    }
                    catch (err) {
                        if (debug) {
                            console.error("".concat(LOG_PREFIX, " Something went wrong attempting to custom attributes on agent: ").concat(agentId, " - ").concat(err.message));
                        }
                    }
                    return [2 /*return*/];
            }
        });
    });
};

/**
 * Check if given agent has been initialized and set on the window.
 *
 * @param agentId
 */
var isNewRelicAvailable = function (agentId) {
    try {
        getAgentFromWindow(agentId, { enforceAgentExists: true });
        return true;
    }
    catch (err) {
        return false;
    }
};
/**
 * Exploit the naturally recursive nature of JSON.stringify, and pass it a
 * replacer function which runs on each nested object during the stringification
 * process
 *
 * see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify#the_replacer_parameter
 */
function deleteInNestedObject(entireObj, keyToFind) {
    JSON.stringify(entireObj, function (_, nestedValue) {
        if (nestedValue && nestedValue[keyToFind]) {
            delete nestedValue[keyToFind];
        }
        return nestedValue;
    });
    return entireObj;
}
/**
 * A generic tracking event to fire PageActions to New Relic.
 *
 * If the given agent has not been initialized the event will not be sent to newrelic.
 *
 * @param agentId
 * @param actionName - The event identifier. Will surface in new relics `actionName` property.
 * Ex NRQL Query: SELECT * FROM PageAction WHERE actionName = 'address-added-successfully'
 * @param eventProperties - a "collection" of all remaining properties sent with the trackEvent object.
 * @param debug - A boolean indicating whether console errors should track issue when sending events.
 */
var trackEvent = function (agentId, actionName, eventProperties, debug) {
    if (eventProperties === void 0) { eventProperties = {}; }
    if (debug === void 0) { debug = false; }
    var agent;
    try {
        agent = getAgentFromWindow(agentId, { enforceAgentExists: true });
    }
    catch (err) {
        if (debug) {
            console.error("".concat(LOG_PREFIX, " Unable to trackEvent: ").concat(actionName, " - ").concat(err.message));
        }
        return;
    }
    try {
        agent.addPageAction(actionName, eventProperties);
    }
    catch (error) {
        if (debug) {
            console.error("".concat(LOG_PREFIX, " Error occurred while tracking new relic event."), { actionName: actionName, eventProperties: eventProperties }, error);
        }
    }
};
/**
 * A generic error tracking function to propagate error occurrences to New Relic.
 *
 * If the given agent has not been initialized the error will not be sent to newrelic.
 *
 * @param agentId
 * @param error - REQUIRED: The JavaScript error during execution.
 * @param errorMetadataPayload - OPTIONAL: Additional metadata to enrich the error's data and presentation in New Relic.
 * OPTIONAL: `errorMetadataPayload.errorName`: to override the error's primary identifier(message). This property reflects as
 * the primary description of Errors in the New Relic Browser's JS Error Panel, and should be accurately named.
 * We suggest keeping the errorName consistent, and excludes dynamic data such as shopperIds, timestamps, etc.
 * @param debug - A boolean indicating whether console errors should track issue when sending events.
 */
var trackError = function (agentId, error, errorMetadataPayload, debug) {
    if (errorMetadataPayload === void 0) { errorMetadataPayload = {}; }
    if (debug === void 0) { debug = false; }
    if (!error)
        return;
    var errorName = errorMetadataPayload.errorName, errorMetadata = __rest(errorMetadataPayload, ["errorName"]);
    var rootErrorMessage = error.message, rootErrorStack = error.stack, rest = __rest(error, ["message", "stack"]);
    var errorIdentifier = errorName || rootErrorMessage || 'unknown-error-id';
    var agent;
    try {
        agent = getAgentFromWindow(agentId, { enforceAgentExists: true });
    }
    catch (err) {
        if (debug) {
            console.error("".concat(LOG_PREFIX, " Unable to trackError: ").concat(errorIdentifier, " - ").concat(err.message));
        }
        return;
    }
    // remove users jwt token from the log
    var originalError = deleteInNestedObject(rest, 'Authorization');
    var modifiedError = new Error(errorIdentifier);
    var errorAttributes = __assign({ rootErrorObject: {
            rootErrorMessage: rootErrorMessage,
            rootErrorStack: rootErrorStack,
        }, originalError: originalError }, errorMetadata);
    try {
        agent.noticeError(modifiedError, errorAttributes);
    }
    catch (err) {
        if (debug) {
            console.error("".concat(LOG_PREFIX, " Error occurred while tracking new relic error."), { modifiedError: modifiedError, errorAttributes: errorAttributes }, err);
        }
    }
};

export { addAdditionalAttributes, initializeNewRelicTrackingContainer, isNewRelicAvailable, trackError, trackEvent };
//# sourceMappingURL=index.esm.js.map
